import React from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { graphql } from 'gatsby';

import { containerStyles } from '../../styles/layout';
import { headers } from '../../styles/type';
import { colors } from '../../theme/colors';
import { media } from '../../theme/media';
import { rem } from '../../utilities/style';

import TextCard from '../TextCard';
import TextRenderer from '../TextRenderer';

const Container = styled.div`
  background-color: ${({ backgroundColor }) => (backgroundColor === 'Blue' ? colors.blue['50'] : colors.white)};
  padding: ${rem(32)} 0;

  ${media('>tiny')} {
    padding: ${rem(56)} 0;
  }
`;

const ContentWrapper = styled.div`
  ${containerStyles.regular}
`;

const Headline = styled.h2`
  ${headers.h3}
  text-align: center;
  color: ${colors.blue['800']};
  margin-bottom: ${rem(40)};
`;

const Grid = styled.div`
  display: grid;
  grid-template-rows: auto;
  grid-template-columns: 1fr;
  gap: ${rem(40)};

  ${({ columns }) => columns && `
    ${media('>small')} {
      grid-template-columns: repeat(2, 1fr);
      gap: ${rem(40)} ${rem(30)};
    }

    ${media('>medium')} {
      grid-template-columns: repeat(${columns}, 1fr);
    }
  `}

  ${({ columns }) => !columns && `
    ${media('>small')} {
      grid-template-columns: repeat(2, 1fr);
    }
  `}
`;

const FeaturedCards = ({ headline, cardItems, backgroundColor, columns, outlined }) => (
  <Container backgroundColor={backgroundColor}>
    <ContentWrapper>
      {headline && (
        <Headline>
          <TextRenderer text={headline} />
        </Headline>
      )}
      <Grid columns={columns}>
        {cardItems?.map((cardItem, index) => (
          <TextCard
            // eslint-disable-next-line react/no-array-index-key
            key={index}
            icon={cardItem.icon}
            subhead={cardItem.subhead}
            headline={cardItem.headline}
            text={cardItem.text}
            primaryCtaText={cardItem.primaryCtaText}
            primaryCtaLink={cardItem.primaryCtaLink}
            secondaryCtaText={cardItem.secondaryCtaText}
            secondaryCtaLink={cardItem.secondaryCtaLink}
            featured={cardItem.featured}
            minHeight={false}
            clickable={cardItem.clickable}
            outlined={outlined}
          />
        ))}
      </Grid>
    </ContentWrapper>
  </Container>
);

FeaturedCards.propTypes = {
  headline: PropTypes.string,
  // eslint-disable-next-line react/forbid-prop-types
  cardItems: PropTypes.arrayOf(
    PropTypes.object
  ),
  backgroundColor: PropTypes.oneOf(['Blue', 'White']),
  columns: PropTypes.string
};

export const query = graphql`
  fragment FeaturedCardsFragment on ContentfulFeaturedCards {
    id
    __typename
    headline
    cardItems {
      id
      icon {
        gatsbyImageData(width: 80, height: 80, quality: 75)
        ...ImageAssetFragment
      }
      subhead
      headline
      text {
        raw
      }
      primaryCtaText
      primaryCtaLink
      secondaryCtaText
      secondaryCtaLink
      featured
      clickable
    }
    backgroundColor
    outlined
    columns
    personalizations {
      ...PersonalizationFragment
    }
  }
`;

export default FeaturedCards;
